import React, { useCallback, useEffect, useRef, useState } from 'react'
import PropTypes from 'prop-types'
import { Box, TextField, Typography } from '@mui/material'
import CheckCircleIcon from '@mui/icons-material/CheckCircle'
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline'
import WarningAmberIcon from '@mui/icons-material/WarningAmber'

const ICONS = {
  success: CheckCircleIcon,
  warning: WarningAmberIcon,
  error: ErrorOutlineIcon
}

/**
 * Popover state for copy-link actions. A notice either auto-dismisses after
 * `durationMs`, or stays open until closed when `persist` is set (used when a
 * link was created but could not be copied, so the user can copy it by hand).
 */
export function useLinkNotice () {
  const [anchor, setAnchor] = useState(null)
  const [notice, setNotice] = useState(null)
  const timer = useRef(null)

  const clearTimer = () => {
    if (timer.current) window.clearTimeout(timer.current)
    timer.current = null
  }

  const close = useCallback(() => {
    clearTimer()
    setAnchor(null)
  }, [])

  const show = useCallback((el, next, { durationMs = 3000, persist = false } = {}) => {
    clearTimer()
    setNotice(next)
    setAnchor(el)
    if (!persist) timer.current = window.setTimeout(() => setAnchor(null), durationMs)
  }, [])

  useEffect(() => clearTimer, [])

  return { anchor, notice, show, close }
}

/**
 * Copies `url`, then reports the outcome. If the browser refuses the write
 * (for example after an awaited request ends the user activation), the URL is
 * shown for manual copying instead of claiming success.
 */
export async function copyWithNotice ({ url, anchor, show, copied, notCopied, durationMs }) {
  try {
    await navigator.clipboard.writeText(url)
    show(anchor, { message: copied, severity: 'success' }, { durationMs })
  } catch (err) {
    console.error(err)
    show(anchor, { message: notCopied, severity: 'warning', url }, { persist: true })
  }
}

LinkNotice.propTypes = {
  notice: PropTypes.shape({
    message: PropTypes.string,
    severity: PropTypes.oneOf(['success', 'warning', 'error']),
    url: PropTypes.string
  })
}

export default function LinkNotice ({ notice }) {
  if (!notice) return null
  const severity = notice.severity || 'success'
  const Icon = ICONS[severity]
  return (
    <Box role="status" sx={{ p: 2, maxWidth: 360 }}>
      <Typography color={`${severity}.main`}>
        <Icon fontSize="small" sx={{ verticalAlign: 'middle', mr: 0.5 }} />
        {notice.message}
      </Typography>
      {notice.url
        ? <TextField
            value={notice.url}
            size="small"
            fullWidth
            autoFocus
            sx={{ mt: 1 }}
            InputProps={{ readOnly: true }}
            inputProps={{ 'aria-label': 'Link to copy' }}
            onFocus={ev => ev.target.select()}
          />
        : null}
    </Box>
  )
}
